import React, { useCallback, useMemo, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  Modal,
  Pressable,
  StyleSheet,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as DocumentPicker from 'expo-document-picker';
import { useTheme } from '../../contexts/ThemeContext';
import { DayApplyScopePicker } from '../DayApplyScopePicker';
import { useCalendarGlassStyles } from './calendarGlassStyles';

const STAGE_FILE_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'image/jpeg',
  'image/png',
  'image/*',
];

export function CalendarStagePanel({
  selectedDateKey,
  onUploadFile,
  uploading = false,
  tabApplyScope,
  setTabApplyScope,
  selectedTourShowDateKeys = [],
  onSave,
  saving = false,
  theme,
  children,
  compact = false,
  onError,
  onSuccess,
}) {
  const { isDarkMode } = useTheme();
  const [importing, setImporting] = useState(false);
  const [importStatus, setImportStatus] = useState('');
  const [importChooserOpen, setImportChooserOpen] = useState(false);
  const glassStyles = useCalendarGlassStyles(theme, isDarkMode);
  const styles = useMemo(() => createStyles(theme, compact), [theme, compact]);

  const runStageImport = useCallback(async () => {
    setImportChooserOpen(false);
    if (!selectedDateKey) {
      onError?.('Select a day first.');
      return;
    }
    try {
      setImporting(true);
      setImportStatus('Opening file picker…');
      const result = await DocumentPicker.getDocumentAsync({
        type: STAGE_FILE_TYPES,
        copyToCacheDirectory: true,
        multiple: false,
      });
      if (result.canceled || !result.assets?.[0]) return;
      setImportStatus('Uploading stage file…');
      await onUploadFile?.(result.assets[0]);
      onSuccess?.('Stage file attached to this day.');
    } catch (err) {
      console.error('stage file import', err);
      onError?.(err?.message || 'Could not import stage file.');
    } finally {
      setImporting(false);
      setImportStatus('');
    }
  }, [onError, onSuccess, onUploadFile, selectedDateKey]);

  const busy = uploading || importing;

  return (
    <View style={styles.wrap}>
      <View style={styles.toolbar}>
        <TouchableOpacity
          style={[styles.toolBtn, styles.toolBtnAccent, busy && styles.toolBtnDisabled]}
          onPress={() => setImportChooserOpen(true)}
          disabled={busy}
        >
          {busy ? (
            <ActivityIndicator size="small" color={theme.brand.accent} />
          ) : (
            <>
              <Ionicons name="cloud-upload-outline" size={14} color={theme.brand.accent} />
              <Text style={[styles.toolBtnText, styles.toolBtnTextAccent]}>Import</Text>
            </>
          )}
        </TouchableOpacity>
      </View>
      {importStatus ? <Text style={styles.importStatus}>{importStatus}</Text> : null}

      <View style={styles.uploadBox}>
        <Ionicons name="cloud-upload-outline" size={24} color={theme.text.secondary} />
        <Text style={styles.uploadTitle}>Upload stage file</Text>
        <Text style={styles.uploadSub}>PDF, DOC, DOCX, JPG, PNG</Text>
        <TouchableOpacity
          style={[styles.attachBtn, busy && styles.attachBtnDisabled]}
          onPress={() => setImportChooserOpen(true)}
          disabled={busy}
        >
          <Ionicons name="attach-outline" size={16} color={theme.brand.accent} />
          <Text style={styles.attachBtnText}>{busy ? 'Uploading…' : 'Attach file to day'}</Text>
        </TouchableOpacity>
      </View>

      {children}

      {selectedDateKey ? (
        <View style={styles.scopeWrap}>
          <DayApplyScopePicker
            compact={compact}
            contained
            showSaveButton
            value={tabApplyScope}
            onChange={setTabApplyScope}
            anchorDateKey={selectedDateKey}
            tourShowDateKeys={selectedTourShowDateKeys}
            theme={theme}
            onSave={onSave}
            saving={saving}
          />
        </View>
      ) : null}

      <Modal visible={importChooserOpen} transparent animationType="fade" onRequestClose={() => setImportChooserOpen(false)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setImportChooserOpen(false)}>
          <Pressable
            style={[styles.modalCard, { backgroundColor: theme.background.card, borderColor: theme.ui.border }]}
            onPress={() => {}}
          >
            <Text style={[styles.modalTitle, { color: theme.text.primary }]}>Import stage file</Text>
            <Text style={[styles.modalHint, { color: theme.text.secondary }]}>
              Attach a stage plot or input list to the selected show day.
            </Text>
            <TouchableOpacity
              style={[styles.importChoiceBtn, glassStyles.cardInset]}
              onPress={() => void runStageImport()}
            >
              <Ionicons name="document-outline" size={18} color={theme.brand.accent} />
              <View style={styles.importChoiceCopy}>
                <Text style={[styles.importChoiceTitle, { color: theme.text.primary }]}>Choose file</Text>
                <Text style={[styles.importChoiceHint, { color: theme.text.secondary }]}>
                  PDF, Word, or image — same formats as the rider import picker.
                </Text>
              </View>
            </TouchableOpacity>
            <TouchableOpacity style={styles.modalCancelBtn} onPress={() => setImportChooserOpen(false)}>
              <Text style={{ color: theme.text.secondary, fontWeight: '700' }}>Cancel</Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

function createStyles(theme, compact) {
  return StyleSheet.create({
    wrap: { gap: compact ? 10 : 12, width: '100%' },
    toolbar: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
    toolBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingHorizontal: 10,
      paddingVertical: 7,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.secondary,
    },
    toolBtnAccent: {
      borderColor: `${theme.brand.accent}55`,
      backgroundColor: `${theme.brand.accent}12`,
    },
    toolBtnDisabled: { opacity: 0.6 },
    toolBtnText: { fontSize: 12, fontWeight: '700', color: theme.text.primary },
    toolBtnTextAccent: { color: theme.brand.accent },
    importStatus: { fontSize: 12, color: theme.brand.accent, fontWeight: '700' },
    uploadBox: {
      alignItems: 'center',
      gap: 6,
      paddingVertical: compact ? 14 : 18,
      paddingHorizontal: 12,
      borderRadius: 12,
      borderWidth: 1,
      borderStyle: 'dashed',
      borderColor: theme.ui.border,
      backgroundColor: theme.background.secondary,
    },
    uploadTitle: { fontSize: 14, fontWeight: '800', color: theme.text.primary },
    uploadSub: { fontSize: 12, color: theme.text.secondary, textAlign: 'center' },
    attachBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      marginTop: 4,
      paddingHorizontal: 14,
      paddingVertical: 10,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: `${theme.brand.accent}66`,
      backgroundColor: `${theme.brand.accent}14`,
    },
    attachBtnDisabled: { opacity: 0.55 },
    attachBtnText: { fontSize: 13, fontWeight: '800', color: theme.brand.accent },
    scopeWrap: {
      marginTop: 14,
      paddingTop: 12,
      paddingHorizontal: 10,
      paddingBottom: 10,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.card,
      minWidth: 0,
      overflow: 'hidden',
    },
    modalBackdrop: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.55)',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
    },
    modalCard: {
      width: '100%',
      maxWidth: 420,
      borderRadius: 14,
      borderWidth: 1,
      padding: 18,
      gap: 12,
    },
    modalTitle: { fontSize: 17, fontWeight: '800' },
    modalHint: { fontSize: 13, lineHeight: 18 },
    importChoiceBtn: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 12,
      padding: 14,
      borderRadius: 12,
      borderWidth: 1,
    },
    importChoiceCopy: { flex: 1, gap: 4 },
    importChoiceTitle: { fontSize: 14, fontWeight: '800' },
    importChoiceHint: { fontSize: 12, lineHeight: 16 },
    modalCancelBtn: { alignSelf: 'flex-end', paddingVertical: 6, paddingHorizontal: 4 },
  });
}

export default CalendarStagePanel;
